/*
 * /tooth-chart — карта зубов и расшифровщик плана лечения.
 *
 * Западные схемы зубов отвечают на вопрос «какой у этого зуба номер».
 * Русский пользователь приходит с другим вопросом: ему выдали бумагу, где
 * написано «2.6 П, вторичный кариес по IV классу, K02.1 — 8 500 ₽», и он не
 * понимает НИЧЕГО, включая то, много это или мало. Поэтому здесь две
 * половины: схема (справочник) и разбор плана (ответ на настоящий вопрос).
 */

import { Odontogram } from './odontogram.js';
import { buildQuestions } from './plan-questions.js';
import { comparePlans } from './plan-compare.js';
import { PlanParser } from './plan-parser.js';

const CFG = window.__TOOTH_CHART || {};
const T = CFG.i18n || {};
const $ = sel => document.querySelector(sel);
const $$ = sel => Array.from(document.querySelectorAll(sel));
// Перевод строки для буфера обмена — отдельной константой.
// Литерал перевода строки внутри исходника дважды съедался правкой
// через генератор, а падало это уже у человека при копировании.
const NL = String.fromCharCode(10);

const state = {
    notation: 'fdi',
    dentition: 'permanent',
    mirrored: false,
    marks: {},
    parsed: null,
};

let chart = null;
let parser = null;

// ── запуск ──────────────────────────────────────────────────────────────

function boot() {
    parser = new PlanParser({ teeth: CFG.teeth, marks: CFG.marks, words: CFG.words,
        procedures: CFG.procedures, icd: CFG.icd });

    chart = new Odontogram($('#tcChart'), {
        teeth: CFG.teeth,
        dentition: state.dentition,
        notation: state.notation,
        labels: { names: CFG.names, chartLabel: T.chart_label },
        onPick: onPick,
    });
    chart.exportCss = CFG.exportCss || '';

    restore();
    wireSegments();
    wirePlan();
    wireQuestionsCopy();
    wireLookup();
    wireExport();

    if (window.__stage) window.__stage('ready');
}

/** Настройки схемы человек меняет один раз и ждёт их при следующем заходе. */
function restore() {
    ['notation', 'dentition'].forEach(k => {
        const saved = window.__toolSettings && window.__toolSettings.load('tooth-chart', k, null);
        if (saved) state[k] = saved;
    });
    const m = window.__toolSettings && window.__toolSettings.load('tooth-chart', 'mirrored', false);
    state.mirrored = !!m;

    chart.setNotation(state.notation);
    chart.setDentition(state.dentition);
    chart.setMirrored(state.mirrored);
    syncSegments();
}

function wireSegments() {
    $$('[data-seg]').forEach(group => {
        const key = group.getAttribute('data-seg');
        group.addEventListener('click', e => {
            const btn = e.target.closest('[data-val]');
            if (!btn) return;
            const val = btn.getAttribute('data-val');
            if (key === 'mirror') {
                state.mirrored = val === '1';
                chart.setMirrored(state.mirrored);
                window.__toolSettings && window.__toolSettings.save('tooth-chart', 'mirrored', state.mirrored);
            } else {
                state[key] = val;
                if (key === 'notation') chart.setNotation(val);
                if (key === 'dentition') chart.setDentition(val);
                window.__toolSettings && window.__toolSettings.save('tooth-chart', key, val);
                // Перерисовка дуги стирает раскраску — возвращаем её.
                chart.setMarks(state.marks);
            }
            syncSegments();
            if (window.__aeFunnel) window.__aeFunnel('setting', { key, val });
        });
    });
}

function syncSegments() {
    $$('[data-seg]').forEach(group => {
        const key = group.getAttribute('data-seg');
        const cur = key === 'mirror' ? (state.mirrored ? '1' : '0') : state[key];
        group.querySelectorAll('[data-val]').forEach(b => {
            const on = b.getAttribute('data-val') === String(cur);
            b.classList.toggle('on', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
    });
}

// ── карточка зуба ───────────────────────────────────────────────────────

function onPick({ fdi, surface }) {
    const t = CFG.teeth[fdi];
    if (!t) return;

    const card = $('#tcCard');
    card.hidden = false;
    $('#tcCardName').textContent = CFG.names[fdi] || fdi;

    $('#tcNumFdi').textContent = t.fdi;
    $('#tcNumUni').textContent = t.universal;
    $('#tcNumPalmer').textContent = t.palmer;

    $('#tcSide').textContent = [
        CFG.labels.arch[t.arch],
        CFG.labels.side[t.side],
    ].join(' · ');

    // Корни и каналы диапазоном: «у вас 3 канала» с последующим четвёртым
    // читается как обман в одном из двух мест.
    const [cMin, cMax] = t.canals;
    $('#tcRoots').textContent = fmt(T.roots_canals, {
        roots: t.roots,
        canals: cMin === cMax ? cMin : `${cMin}–${cMax}`,
    });

    $('#tcErupt').textContent = eruptText(t);

    // Смена зубов — то, что чаще всего пугает родителей.
    const rel = $('#tcRelation');
    if (t.primary && t.succeeded_by) {
        rel.hidden = false;
        rel.textContent = fmt(T.rel_will_be_replaced, { tooth: CFG.names[t.succeeded_by] || t.succeeded_by });
    } else if (!t.primary && t.succeeds) {
        rel.hidden = false;
        rel.textContent = fmt(T.rel_replaces, { tooth: CFG.names[t.succeeds] || t.succeeds });
    } else if (!t.primary) {
        rel.hidden = false;
        // Именно это делает «шестёрку» самым запущенным зубом: родители ждут,
        // что она выпадет, и не лечат её.
        rel.textContent = T.rel_no_predecessor;
    } else {
        rel.hidden = true;
    }

    // Поверхности зуба + подсветка выбранной.
    const list = $('#tcSurfaces');
    list.textContent = '';
    t.surfaces.forEach(s => {
        const li = document.createElement('li');
        li.className = 'tc-surf' + (s === surface ? ' on' : '');
        li.textContent = CFG.labels.surface[s] || s;
        list.appendChild(li);
    });

    // Что уже сказал про этот зуб разобранный план.
    const found = state.parsed && state.parsed.teeth[fdi];
    const box = $('#tcFindings');
    box.hidden = !found;
    if (found) {
        box.textContent = '';
        found.findings.forEach(f => box.appendChild(chip(CFG.labels.finding[f] || f, severityOf(f))));
        found.icd.forEach(c => box.appendChild(chip(c + (CFG.labels.icd[c] ? ' — ' + CFG.labels.icd[c] : ''), 'neutral')));
        if (found.price) box.appendChild(chip(money(found.price), 'price'));
    }

    if (window.__aeFunnel) window.__aeFunnel('tooth_picked', { fdi, surface: surface || '' });
}

function eruptText(t) {
    const [a, b] = t.erupt;
    const window_ = monthsRange(a, b);
    if (!t.primary) return fmt(T.erupt_permanent, { window: window_ });
    const [sa, sb] = t.shed;
    return fmt(T.erupt_primary, { window: window_, shed: monthsRange(sa, sb) });
}

function monthsRange(a, b) {
    // До двух лет человек думает месяцами, дальше — годами. Смешивать
    // единицы в одной фразе нельзя: «прорежется в 84–96 месяцев» никто
    // не переводит в уме.
    if (b <= 30) return fmt(T.months, { from: a, to: b });
    const y = n => Math.round(n / 12 * 10) / 10;
    return fmt(T.years, { from: y(a), to: y(b) });
}

function chip(text, kind) {
    const s = document.createElement('span');
    s.className = 'tc-chip tc-chip-' + (kind || 'neutral');
    s.textContent = text;
    return s;
}

/** Важность работы: почти всегда «нужно лечить» — это позиция плана. */
function procSeverity(pr) {
    return (pr && pr.severity) || 'urgent';
}

function severityOf(findingKey) {
    for (const w of (CFG.words || [])) if (w.key === findingKey) return w.severity;
    for (const mark in CFG.marks) {
        if (CFG.marks[mark].key === findingKey) return CFG.marks[mark].severity;
    }
    return 'neutral';
}

// ── разбор плана ────────────────────────────────────────────────────────

function wirePlan() {
    const area = $('#tcPlan');
    const run = () => analyse(area.value);

    let timer = null;
    area.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(run, 220);
    });
    $('#tcPlanClear').addEventListener('click', () => {
        area.value = '';
        analyse('');
        area.focus();
    });
    $$('[data-sample]').forEach(b => b.addEventListener('click', () => {
        area.value = b.getAttribute('data-sample');
        analyse(area.value);
        if (window.__aeFunnel) window.__aeFunnel('sample_used');
    }));

    // Черновик плана переживает перезагрузку: человек печатает его с
    // бумажки, и потерять набранное на середине — потерять его целиком.
    try {
        const draft = localStorage.getItem('tooth-chart:plan');
        if (draft) { area.value = draft; analyse(draft); }
    } catch (_) {}
    area.addEventListener('input', () => {
        try { localStorage.setItem('tooth-chart:plan', area.value); } catch (_) {}
    });

    // ── второй план: второе мнение ──────────────────────────────────
    const areaB = $('#tcPlanB');
    if (!areaB) return;
    let timerB = null;
    const runB = () => {
        try { localStorage.setItem('tooth-chart:planB', areaB.value); } catch (_) {}
        renderCompare();
    };
    areaB.addEventListener('input', () => {
        clearTimeout(timerB);
        timerB = setTimeout(runB, 220);
    });
    $('#tcPlanBClear').addEventListener('click', () => {
        areaB.value = '';
        runB();
        areaB.focus();
    });
    try {
        const draftB = localStorage.getItem('tooth-chart:planB');
        if (draftB) {
            areaB.value = draftB;
            // Свёрнутый <details> с уже набранным текстом — это спрятанная
            // от человека его же работа: раскрываем.
            const box = $('#tcSecondBox');
            if (box) box.open = true;
        }
    } catch (_) {}
}

function analyse(text) {
    const res = parser.parse(text);
    state.parsed = res;

    // Разметка схемы.
    const marks = {};
    for (const fdi in res.teeth) {
        const t = res.teeth[fdi];
        // Худшая из находок зуба — чтобы «кариес + пломба» красился как
        // кариес, а не как «уже сделано».
        /*
         * ⚠️ Работа считается наравне с состоянием. «4.8 ретенция, удаление»
         * до 09.09.2026 красилось серым «нет зуба»: тул видел только
         * состояние и прятал от человека четыре операции.
         */
        const rank = { urgent: 3, attention: 2, done: 1, missing: 1, neutral: 0 };
        let sev = [...t.findings.map(severityOf), ...(t.procedures || []).map(procSeverity)]
            .sort((a, b) => (rank[b] || 0) - (rank[a] || 0))[0] || null;
        /*
         * ⚠️ У ОТСУТСТВУЮЩЕГО зуба заливка остаётся «нет зуба», даже когда на
         * него запланирована работа: лечить нечего, там пусто. Иначе
         * «4.6 О — имплантация» красилась красным «нужно лечить» и
         * противоречила собственной легенде — так и вышло при первой
         * версии этой правки, поймано глазами на скриншоте.
         * Работу на таком месте показывает пунктир (data-planned).
         */
        if (t.whole === 'missing') sev = 'missing';
        marks[fdi] = {
            whole: t.whole, surfaces: t.surfaces, severity: sev,
            planned: (t.procedures || []).length > 0,
        };
    }
    state.marks = marks;
    chart.setMarks(marks);
    chart.highlight(Object.keys(res.teeth));

    renderPlan(res);
    renderQuestions(res);
    renderCompare();

    // Смешанный прикус подсказываем, а не навязываем: если в плане только
    // молочные зубы, а на схеме постоянные, человек увидит пустую дугу и
    // решит, что тул сломан.
    const anyPrimary = Object.keys(res.teeth).some(f => CFG.teeth[f] && CFG.teeth[f].primary);
    const anyPermanent = Object.keys(res.teeth).some(f => CFG.teeth[f] && !CFG.teeth[f].primary);
    const hint = $('#tcDentitionHint');
    const need = anyPrimary && anyPermanent ? 'mixed' : (anyPrimary ? 'primary' : null);
    if (need && need !== state.dentition) {
        hint.hidden = false;
        hint.querySelector('button').setAttribute('data-switch', need);
        hint.querySelector('span').textContent = need === 'mixed' ? T.hint_mixed : T.hint_primary;
    } else {
        hint.hidden = true;
    }
}

function renderPlan(res) {
    const out = $('#tcPlanOut');
    const empty = $('#tcPlanEmpty');
    const count = Object.keys(res.teeth).length;

    empty.hidden = count > 0;
    out.textContent = '';
    if (!count) { $('#tcPlanSummary').hidden = true; return; }

    // Сводка: сколько зубов, что срочно, сколько денег. Именно этих трёх
    // чисел нет в бумаге, которую выдала клиника.
    /*
     * Четыре плитки отвечают на четыре РАЗНЫХ вопроса, поэтому и считаются
     * по-разному: «нужно лечить» — это НАЙДЕННАЯ проблема (состояние),
     * «запланированы работы» — то, что собираются сделать. Если считать обе
     * по худшему из двух, плитки сливаются: у образцового плана обе давали
     * бы 8 из 8 и не сообщали ничего.
     */
    const urgent = [], attention = [], done = [], planned = [];
    for (const fdi in res.teeth) {
        const t = res.teeth[fdi];
        const sev = t.findings.map(severityOf);
        if ((t.procedures || []).length) planned.push(fdi);
        if (sev.includes('urgent')) urgent.push(fdi);
        else if (sev.includes('attention')) attention.push(fdi);
        else done.push(fdi);
    }

    const sum = $('#tcPlanSummary');
    sum.hidden = false;
    sum.textContent = '';
    sum.appendChild(tile(T.sum_teeth, String(count)));
    if (urgent.length) sum.appendChild(tile(T.sum_urgent, String(urgent.length), 'urgent'));
    if (attention.length) sum.appendChild(tile(T.sum_attention, String(attention.length), 'attention'));
    if (planned.length) sum.appendChild(tile(T.sum_planned, String(planned.length), 'planned'));
    if (res.total) sum.appendChild(tile(T.sum_total, money(res.total, res.currency)));

    // Построчный разбор — рядом с исходной строкой, чтобы человек видел,
    // ЧТО именно тул прочитал. Разбор, который нельзя сверить с оригиналом,
    // приходится принимать на веру, а речь про его зубы и его деньги.
    for (const line of res.lines) {
        const row = document.createElement('div');
        row.className = 'tc-line';

        const src = document.createElement('div');
        src.className = 'tc-line-src';
        src.textContent = line.text;
        row.appendChild(src);

        const read = document.createElement('div');
        read.className = 'tc-line-read';

        line.teeth.forEach(fdi => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'tc-chip tc-chip-tooth';
            b.textContent = (CFG.names[fdi] || fdi) + ' · ' + numberFor(fdi);
            b.addEventListener('click', () => { chart.select(fdi); onPick({ fdi, surface: null }); });
            read.appendChild(b);
        });
        line.findings.forEach(f => read.appendChild(chip(CFG.labels.finding[f] || f, severityOf(f))));
        (line.procedures || []).forEach(pr => read.appendChild(
            chip((CFG.labels.procedure && CFG.labels.procedure[pr.key]) || pr.key, 'planned')));
        line.surfaces.forEach(s => read.appendChild(chip(CFG.labels.surface[s] || s, 'neutral')));
        if (line.blackClass) read.appendChild(chip(fmt(T.black_class, { n: line.blackClass }), 'neutral'));
        line.icd.forEach(c => read.appendChild(chip(c + (CFG.labels.icd[c] ? ' — ' + CFG.labels.icd[c] : ''), 'neutral')));
        if (line.price) read.appendChild(chip(money(line.price, res.currency), 'price'));

        row.appendChild(read);
        out.appendChild(row);
    }

    /*
     * ⚠️ `unread` — главное число этого тула, и до 09.09.2026 его не
     * существовало. «Разобрано 5 строк» ничего не говорит: строка может
     * быть разобрана и БЕЗ вердикта (зуб найден, а что с ним — нет).
     * Именно этот процент показывает, каких формулировок не хватает
     * словарю; без него разбор улучшается только наугад.
     *
     * ⛔ Сам ТЕКСТ строки не отправляется никогда — это медицинские данные.
     * Едет только форма: были ли цена и код МКБ и сколько в строке слов.
     */
    const unread = res.lines.filter(l => l.teeth.length
        && !l.findings.length && !(l.procedures || []).length);
    const procs = res.lines.reduce((n, l) => n + (l.procedures || []).length, 0);
    if (window.__aeFunnel) {
        window.__aeFunnel('plan_parsed', {
            teeth: count, lines: res.lines.length, priced: res.total > 0 ? 1 : 0,
            unread: unread.length, procs,
        });
    }
    for (const l of unread.slice(0, 3)) {
        if (window.__aeDiagnostic) {
            window.__aeDiagnostic({
                issue: 'plan_line_unread',
                priced: l.price ? 1 : 0,
                icd: l.icd.length ? 1 : 0,
                words: (l.text.match(/[А-Яа-яЁёA-Za-z]{3,}/g) || []).length,
            });
        }
    }
}

function numberFor(fdi) {
    const t = CFG.teeth[fdi];
    if (!t) return fdi;
    return state.notation === 'universal' ? t.universal
        : state.notation === 'palmer' ? t.palmer : t.fdi;
}

/* ── вопросы врачу ──────────────────────────────────────────────────── */

function nameOf(fdi) {
    return (CFG.names[fdi] || fdi) + ' · ' + numberFor(fdi);
}

function renderQuestions(res) {
    const box = $('#tcQaBox');
    if (!box) return;
    const qs = buildQuestions(res, T, nameOf, numberFor);
    const list = $('#tcQa');
    list.textContent = '';
    box.hidden = qs.length === 0;
    if (!qs.length) return;

    for (const q of qs) {
        const li = document.createElement('li');
        li.textContent = q;
        list.appendChild(li);
    }
    if (window.__aeFunnel) window.__aeFunnel('questions_shown', { n: qs.length });
}

function wireQuestionsCopy() {
    const btn = $('#tcQaCopy');
    if (!btn) return;
    btn.addEventListener('click', async () => {
        const qs = [...$('#tcQa').children].map(li => '— ' + li.textContent);
        if (!qs.length) return;
        try {
            await navigator.clipboard.writeText([T.qa_head, ...qs].join(NL));
            window.__toast && window.__toast(T.qa_copied, 'success');
            if (window.__aeFunnel) window.__aeFunnel('questions_copied', { n: qs.length });
        } catch (e) {
            window.__handleError(e, 'tooth-chart:questions');
        }
    });
}

/* ── сравнение двух планов ──────────────────────────────────────────── */

function renderCompare() {
    const box = $('#tcCmpBox');
    const areaB = $('#tcPlanB');
    if (!box || !areaB) return;

    const textB = areaB.value.trim();
    if (!textB || !state.parsed) { box.hidden = true; return; }

    const b = parser.parse(textB);
    if (!Object.keys(b.teeth).length) { box.hidden = true; return; }

    const cmp = comparePlans(state.parsed, b);
    box.hidden = false;

    const sum = $('#tcCmpSummary');
    sum.textContent = '';
    if (cmp.onlyA) sum.appendChild(tile(T.cmp_only_a, String(cmp.onlyA)));
    if (cmp.onlyB) sum.appendChild(tile(T.cmp_only_b, String(cmp.onlyB)));
    if (cmp.differ) sum.appendChild(tile(T.cmp_differ, String(cmp.differ), 'attention'));
    if (cmp.same) sum.appendChild(tile(T.cmp_same, String(cmp.same)));

    /*
     * ⚠️ Разница сумм показывается ТОЛЬКО когда валюта одна. Планы из
     * разных стран (или из клиники, пишущей в долларах) складывать
     * нельзя, и молча вычесть одно из другого значило бы выдать
     * бессмыслицу за ответ.
     */
    if (cmp.mixedCurrency) {
        const warn = document.createElement('p');
        warn.className = 'tcx-sub';
        warn.textContent = T.cmp_mixed_currency;
        sum.appendChild(warn);
    } else if (cmp.totalA || cmp.totalB) {
        sum.appendChild(tile(T.cmp_total_a, money(cmp.totalA, cmp.currency)));
        sum.appendChild(tile(T.cmp_total_b, money(cmp.totalB, cmp.currency)));
        const d = cmp.diff;
        const label = d > 0 ? T.cmp_diff_more : (d < 0 ? T.cmp_diff_less : T.cmp_diff_same);
        sum.appendChild(tile(label, d === 0 ? '—' : money(Math.abs(d), cmp.currency),
            d > 0 ? 'urgent' : (d < 0 ? 'planned' : null)));
    }

    const out = $('#tcCmpOut');
    out.textContent = '';
    // Совпавшие зубы не показываем построчно: список из тридцати строк
    // «одинаково» прячет те четыре, ради которых сюда пришли.
    for (const r of cmp.rows.filter(x => x.kind !== 'same')) {
        const row = document.createElement('div');
        row.className = 'tcx-cmp-row k-' + r.kind;

        const head = document.createElement('button');
        head.type = 'button';
        head.className = 'tc-chip tc-chip-tooth';
        head.textContent = nameOf(r.fdi);
        head.addEventListener('click', () => { chart.select(r.fdi); onPick({ fdi: r.fdi, surface: null }); });
        row.appendChild(head);

        const sideA = document.createElement('span');
        sideA.className = 'tcx-cmp-side';
        sideA.textContent = fmt(T.cmp_row_a, { what: describe(r.findA, r.procA) });
        row.appendChild(sideA);

        const sideB = document.createElement('span');
        sideB.className = 'tcx-cmp-side';
        sideB.textContent = fmt(T.cmp_row_b, { what: describe(r.findB, r.procB) });
        row.appendChild(sideB);

        for (const k of r.addedInB) row.appendChild(chip(fmt(T.cmp_added, { what: procLabel(k) }), 'planned'));
        for (const k of r.droppedInB) row.appendChild(chip(fmt(T.cmp_dropped, { what: procLabel(k) }), 'attention'));

        out.appendChild(row);
    }

    if (window.__aeFunnel) {
        window.__aeFunnel('plans_compared', {
            differ: cmp.differ, onlyA: cmp.onlyA, onlyB: cmp.onlyB,
        });
    }
}

function procLabel(k) {
    return (CFG.labels.procedure && CFG.labels.procedure[k]) || k;
}

/** Человеческое описание половины строки сравнения. */
function describe(finds, procs) {
    const parts = [
        ...finds.map(f => CFG.labels.finding[f] || f),
        ...procs.map(procLabel),
    ];
    return parts.length ? parts.join(', ') : '—';
}

function tile(label, value, kind) {
    const d = document.createElement('div');
    d.className = 'tc-tile' + (kind ? ' tc-tile-' + kind : '');
    const v = document.createElement('b');
    v.textContent = value;
    const l = document.createElement('span');
    l.textContent = label;
    d.append(v, l);
    return d;
}

function money(n, currency) {
    const c = currency || '₽';
    return Math.round(n).toLocaleString(window.__LOCALE || 'ru') + ' ' + c;
}

// ── поиск по номеру ─────────────────────────────────────────────────────

function wireLookup() {
    const input = $('#tcLookup');
    const say = $('#tcLookupSay');

    input.addEventListener('input', () => {
        const raw = input.value.trim().replace(/\s|\./g, '').toUpperCase();
        if (!raw) { say.textContent = ''; return; }

        // Ищем во всех трёх нотациях сразу: человек не знает, в какой
        // записан его номер, и спрашивать его об этом — перекладывать на
        // него нашу работу.
        let hit = CFG.teeth[raw] ? raw : null;
        if (!hit) {
            for (const fdi in CFG.teeth) {
                const t = CFG.teeth[fdi];
                if (String(t.universal).toUpperCase() === raw) { hit = fdi; break; }
            }
        }
        if (!hit) {
            for (const fdi in CFG.teeth) {
                if (CFG.teeth[fdi].palmer.replace(/[┘└┌┐]/g, '') === raw) { hit = fdi; break; }
            }
        }

        if (hit) {
            say.textContent = '';
            chart.select(hit);
            onPick({ fdi: hit, surface: null });
        } else {
            say.textContent = T.lookup_none;
        }
    });
}

// ── экспорт ─────────────────────────────────────────────────────────────

function wireExport() {
    $('#tcSave').addEventListener('click', async () => {
        try {
            const blob = await chart.toBlob(2, getComputedStyle(document.body).backgroundColor || '#0f1621');
            const name = window.__outputName
                ? window.__outputName.join(['tooth-chart', stamp()], { ext: 'png' })
                : 'tooth-chart.png';
            window.__downloadFile(blob, name);
            if (window.__aeToolComplete) window.__aeToolComplete({ ok: 1, kind: 'png' });
        } catch (e) {
            window.__handleError(e, 'tooth-chart:save');
        }
    });

    $('#tcPrint').addEventListener('click', () => window.print());
}

function stamp() {
    const d = new Date();
    const p = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ── мелочи ──────────────────────────────────────────────────────────────

function fmt(tpl, vars) {
    return String(tpl || '').replace(/:(\w+)/g, (m, k) => (k in vars ? String(vars[k]) : m));
}

document.addEventListener('click', e => {
    const b = e.target.closest('[data-switch]');
    if (!b) return;
    const val = b.getAttribute('data-switch');
    state.dentition = val;
    chart.setDentition(val);
    chart.setMarks(state.marks);
    syncSegments();
    $('#tcDentitionHint').hidden = true;
});

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}
